import useStore from "../../../store"
import { useTranslation } from "react-i18next"
import InfoItem from "../../common/InfoItem"
import formatter from "../../../constants/currencyFormatter"
import UnitTitleDownload from "./UnitTitleDownload"
import UnitAchRescheduleRequest from "./UnitAchRescheduleRequest"
import { DateTime } from "luxon"
import Spinner from "../../common/Spinner"
import useUserType from "../../../hooks/useUserType"
import MarkSoldUnsold from "./MarkSoldUnsold"
import MarkArbitrated from "./MarkArbitrated"
import MarkUnarbitrated from "./MarkUnarbitrated"
import { isArbitrated } from "../../../utilities/unitUtilities"
import UploadBos from "./UploadBos"
import UnitTitleRequest from "./UnitTitleRequest"
import { Widget, WidgetHeader, WidgetBody } from "../../ui/Widget"
import AuditButton from "../../VCI/Dealers/AuditButton"
import ConditionReportButton from "./ConditionReportButton"
import TransportUnitButton from "./TransportUnitButton"

export default function UnitInfo() {
  const { t } = useTranslation()
  const { unit, amountDueOn, createPaymentStatus, dealer, transfer } = useStore()

  const dueOnDate = DateTime.fromISO(unit?.attributes['due-on'])
  const isPastDue = dueOnDate < DateTime.now()
  const { isEmployee } = useUserType()
  const isAbleToRequestAchReschedule = (
    unit.attributes['ach-initiated'] &&
    transfer != null &&
    unit.attributes['pending-transfer-id']?.toString() === transfer.id &&
    transfer.attributes["transfer-change-request"] == null &&
    transfer.attributes["status"] == 1
  )
  const isTransportation = unit?.attributes?.transportation

  const handlePaymentAmount = () => {
    if (unit?.attributes["ach-initiated"]) {
      return "Transfer Pending"
    }
    if (amountDueOn?.id?.toString() === unit?.id) {
      return formatter.format(amountDueOn.payoff)
    }
    return <Spinner height="20" color="white" />
  }

  const handlePrincipalOrTransportationFee = () => {
    if (isTransportation) {
      if (amountDueOn?.id?.toString() === unit?.id) {
        return formatter.format(amountDueOn.fees)
      }
      return <Spinner height="20" color="white" />
    }
    return formatter.format(unit?.attributes['original-principal'])
  }

  return (
    <Widget>
      <WidgetHeader title={t('Unit Information')} />
      <WidgetBody
        isVisible={createPaymentStatus === 'idle'}
      >
        <div className="grid grid-cols-2 w-full text-left p-2 bg-grey-900 rounded">
          <InfoItem
            title={isTransportation ? t("Transportation Fee") : t("Original Principal")}
            data={handlePrincipalOrTransportationFee()}
          />
          <InfoItem
            title={t("Payoff Amount")}
            data={handlePaymentAmount()}
          />
          <InfoItem
            title={t('Due On')}
            data={dueOnDate.toLocaleString(DateTime.DATE_MED)}
            alert={isPastDue}
          />
          <InfoItem
            title={'VIN'}
            data={unit.attributes.vin}
          />
          <InfoItem
            title={t('Color')}
            data={unit.attributes.color}
          />
          <InfoItem
            title={t('Stock Number')}
            data={unit.attributes['stock-number']}
          />
          <InfoItem
            title={t('Days')}
            data={unit.attributes['days-floored']}
          />
          <InfoItem
            title={t('Refloors Remaining')}
            data={unit.attributes['maximum-refloors'] - (unit.attributes['current-floor'] - 1)}
          />

          {isEmployee &&
            <>
              <InfoItem
                title={'Title'}
                data={unit.attributes['title-status']}
              />
              <InfoItem
                title={'Days Since Last Touch'}
                data={unit.attributes['days-since-last-touch']}
                alert={unit.attributes['days-since-last-touch'] >= 40 || unit.attributes['days-since-last-touch'] === 'Never Touched'}
              />
              <InfoItem
                title={'Advance To'}
                data={unit.attributes['advance-to'] ?? 'Unknown'}
              />
              {unit.attributes['current-location'] &&
                <InfoItem
                  title={'Current Location'}
                  data={unit.attributes['current-location']}
                />
              }
              {unit.attributes['location-detail'] &&
                <InfoItem
                  title={'Location Detail'}
                  data={unit.attributes['location-detail']}
                />
              }
            </>
          }
          {unit.attributes.sold &&
            <InfoItem
              title={t('Sold On')}
              data={unit?.attributes['sold-on']}
            />
          }
          <div className="col-span-2">
            <div className="flex flex-col mt-2 gap-2">
              {!isTransportation &&
                <>
                  <UnitTitleDownload unit={unit} />

                  <MarkSoldUnsold unit={unit} />

                  {isEmployee &&
                    (isArbitrated(unit) ? <MarkUnarbitrated unit={unit} /> : <MarkArbitrated unit={unit} />)
                  }

                  {unit.attributes.sold && <UploadBos unit={unit} />}

                  <UnitTitleRequest unit={unit} />

                  <TransportUnitButton unit={unit} />
                </>
              }

              {isAbleToRequestAchReschedule &&
                <UnitAchRescheduleRequest transfer={transfer} />
              }

              {!isTransportation && isEmployee &&
                <>
                  <AuditButton
                    dealer={dealer}
                    unitIds={[unit.id]}
                    auditType={3}
                  />
                  <ConditionReportButton />
                </>
              }
            </div>
          </div>
        </div>
      </WidgetBody>
    </Widget>
  )
}